import React, { useEffect, useState } from 'react'
import className from 'classnames/bind'
import { useHistory } from 'react-router'
import { delay, isEmpty } from 'lodash'

import style from './MyhouseNoticeEnrolMain.module.scss'

import MyhouseNoticeEnrolSelect from '@Components/atoms/MyhouseNoticeEnrolSelect'
import MyhouseNoticeEnrolTitle from '@Components/atoms/MyhouseNoticeEnrolTitle'
import MyhouseContents from '@Components/atoms/MyhouseContents'
import MyhouseImageAttach from '@Components/atoms/MyhouseImageAttach'
import MyhouseSwitch from '@Components/atoms/MyhouseSwitch'
import { MY_HOUSE_BOARD_TYPE, MY_HOUSE_NOTICE_CATEGORY, MY_HOUSE_NOTICE_CATEGORY_LIST, MY_HOUSE_NOTICE_CATEGORY_OBJ } from '@Root/contants/myhouse'
import useBoardRegister from '@Hooks/myhouse/board/useBoardRegister'
import useBoardImageRegister from '@Hooks/myhouse/board/useBoardImageRegister'
import { MODAL_TYPE } from '@Root/contants/myhouseModal'
import useModal from '@Hooks/myhouse/useModal'
import useDebounce from '@Hooks/common/useDebounce'
import { FETCH_STATUS } from '@Root/contants'
import { TOAST_AFTER_EVENT, TOAST_TEXT } from '@Root/contants/myhouseToast'
import useToast from '@Hooks/myhouse/useToast'
import MyhouseCommunityRegistGuide from '@Components/atoms/MyhouseCommunityRegistGuide'
import MyhouseCommonButton from '@Components/atoms/MyhouseCommonButton'
import { LAND_SERVICE_PREFIX_URL } from '@Root/contants/urlConstants'
import MyhouseReportPolicyGuide from '@Components/atoms/MyhouseReportPolicyGuide'
import { NCLICK, NDS_PROTOCOL } from '@Root/contants/lcs'
import { handleNclick } from '@Helpers/nclick'
import { lcsSend } from '@Helpers/lcs'

const cx = className.bind(style)

const MyhouseNoticeEnrolMain: React.FC = () => {
  const {
    reduxBoardRegister,
    reduxMyhouse,
    loadBoardRegister,
    setSelectCategory,
    setTitle,
    setContents,
    setNoticeYn,
    setOpeningYn,
    setCopyYn,
    setInitialState,
    setBbsTypeCode,
    initFetchStatus,
    loadPenalty,
  } = useBoardRegister()

  const { files, previewURLs, handleImageChange, handleRemoveClick, getFileSize } = useBoardImageRegister()
  const { landDebounce } = useDebounce()
  const debouncedLoadBoardRegister = landDebounce(loadBoardRegister)
  const history = useHistory()
  const [noticePossible, setNoticePossible] = useState(false)
  const [isTooltip, setIsTooltip] = useState((localStorage.getItem('myhouse:bbs:attach:tooltip:close') || 'N') === 'N')

  const { bbsTypeCode, category, title, contents, noticeYn, openingYn, copyYn, fetchStatus: registerFetchStatus, percentage, penalty } = reduxBoardRegister
  const { complexNo } = reduxMyhouse
  const { open } = useModal()
  const { show } = useToast()

  const categoryList = MY_HOUSE_NOTICE_CATEGORY_LIST.filter((element, index) => index > 0).map((item) => {
    return {
      text: MY_HOUSE_NOTICE_CATEGORY_OBJ[item.CODE].ENROLL_LABEL,
      category: item.CODE,
    }
  })

  useEffect(() => {
    lcsSend(NDS_PROTOCOL.MY_HOUSE.NOTICE.ENROL)

    window.scrollTo(0, 0)
    setBbsTypeCode('NOTICE')
    loadPenalty()

    return () => {
      setInitialState()
      initFetchStatus()
    }
  }, [])

  useEffect(() => {
    if (penalty.fetchStatus === FETCH_STATUS.SUCCESS && penalty.code) {
      history.replace(LAND_SERVICE_PREFIX_URL.MYHOUSE_PENALTY)
      return
    }
  }, [penalty.fetchStatus])

  useEffect(() => {
    if (registerFetchStatus === FETCH_STATUS.SUCCESS) {
      show(TOAST_TEXT.ARTICLE_POSTED, TOAST_AFTER_EVENT.HISTORY_BACK)
    }
  }, [registerFetchStatus])

  const handleClick = (e: React.SyntheticEvent) => {
    e.preventDefault()
    handleNclick(NCLICK.MY_HOUSE.NOTICE_ENROL.COMPLETE, e)

    if (registerFetchStatus === FETCH_STATUS.PENDING) {
      return
    }

    if (isEmpty(category)) {
      open(MODAL_TYPE.CATEGORY_REQUIRED)
      return
    }

    if (isEmpty(title) || isEmpty(title.trim())) {
      open(MODAL_TYPE.TITLE_REQUIRED)
      return
    }

    if ((isEmpty(contents) || isEmpty(contents.trim())) && isEmpty(files)) {
      open(MODAL_TYPE.CONTENT_OR_IMAGE_REQUIRED)
      return
    }

    const param = {
      complexNo,
      bbsTypeCode,
      category,
      title,
      contents,
      noticeYn,
      openingYn,
      copyYn,
    }
    debouncedLoadBoardRegister(param, files)
  }

  useEffect(() => {
    if (![MY_HOUSE_NOTICE_CATEGORY.OFFICE, MY_HOUSE_NOTICE_CATEGORY.MEETING, MY_HOUSE_NOTICE_CATEGORY.GOVERNMENT].includes(category as MY_HOUSE_NOTICE_CATEGORY)) {
      setNoticeYn(false)
      setNoticePossible(false)
    } else {
      setNoticePossible(true)
    }
  }, [category])

  if (registerFetchStatus === FETCH_STATUS.PENDING && files.length > 0) {
    return <MyhouseCommunityRegistGuide percentage={percentage} />
  }

  return (
    <>
      <MyhouseNoticeEnrolSelect selectItem={categoryList} handleChange={setSelectCategory} initialCategory={category} />
      <MyhouseNoticeEnrolTitle handleBlur={setTitle} value={title} />
      <MyhouseContents handleBlur={setContents} content={contents} boardType={MY_HOUSE_BOARD_TYPE.NOTICE} />
      <MyhouseImageAttach
        previewURLs={previewURLs}
        handleImageChange={handleImageChange}
        handleRemoveClick={handleRemoveClick}
        fileSize={getFileSize()}
        isTooltip={isTooltip}
        setIsTooltip={setIsTooltip}
        boardType={MY_HOUSE_BOARD_TYPE.NOTICE}
      />
      <div className={cx('wrap_switch')}>
        {noticePossible && <MyhouseSwitch description="필독 공지사항으로 등록" isInit={noticeYn} setChecked={setNoticeYn} nclickChange={NCLICK.MY_HOUSE.NOTICE_ENROL.IMPORTANT} />}
        <MyhouseSwitch
          description="단지 입주민에게만 공개"
          isInit={openingYn}
          setChecked={setOpeningYn}
          isTooltip
          tipTitle={'게시글 공개 설정'}
          tipDescription={'설정 시, 해당 게시글은 우리단지 집주인 인증자 또는 입주민 인증자에게만 공개됩니다.'}
          nclickChange={NCLICK.MY_HOUSE.NOTICE_ENROL.SECURITY}
          nclickTooltip={NCLICK.MY_HOUSE.NOTICE_ENROL.SECURITY_TOOLTIP}
        />
        <MyhouseSwitch description="복사 허용" isInit={copyYn} setChecked={setCopyYn} nclickChange={NCLICK.MY_HOUSE.NOTICE_ENROL.COPYABLE} />
      </div>
      <MyhouseReportPolicyGuide className={cx('guide')} boardType={MY_HOUSE_BOARD_TYPE.NOTICE} />
      <MyhouseCommonButton buttonText="등록하기" isFloating onClick={handleClick} />
    </>
  )
}

export default MyhouseNoticeEnrolMain
